/** OPTION E — Living Lifecycle.
 *  A subtle "wow" moment: a single word inside the executive headline morphs
 *  through the six lifecycle stages, while a hairline marker glides along a
 *  procure→reuse rail beneath.  Everything else stays Executive-Minimal calm.
 *  All motion is CSS-only and disabled under prefers-reduced-motion.
 */
import { ArrowRight, Loader2, ChevronLeft, Activity } from "lucide-react";
import { Link } from "react-router-dom";
import { useLoginAuth } from "./useLoginAuth";

const STAGES = ["Procured", "Deployed", "Managed", "Supported", "Recovered", "Reused", "Recycled"];

export default function OptionE() {
  const a = useLoginAuth();
  return (
    <div
      className="min-h-screen bg-[color:var(--st-bg)] text-[color:var(--st-text)] flex flex-col"
      data-testid="login-option-e"
    >
      {/* Header — matches production header */}
      <header className="border-b border-[color:var(--st-border)]">
        <div className="max-w-6xl mx-auto flex items-center h-14 px-6 md:px-8">
          <div className="text-[13px] font-semibold tracking-[0.14em]">SUNTRONIX ONE</div>
          <span className="mx-3 text-[color:var(--st-text-hint)]">·</span>
          <span className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">
            Global Technology Operations Platform
          </span>
          <div className="ml-auto flex items-center gap-4 text-[11px] text-[color:var(--st-text-muted)]">
            <a href="/help/status" className="hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
              <Activity className="h-3 w-3" strokeWidth={1.5} /> System status
            </a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </header>

      <main className="flex-1">
        <div className="max-w-6xl mx-auto grid lg:grid-cols-12 gap-8 lg:gap-16 px-6 md:px-8 pt-10 md:pt-20 pb-12">
          {/* BRAND SIDE — the living moment */}
          <div className="lg:col-span-7 flex flex-col justify-between min-h-[60vh]">
            <div />
            <div className="max-w-xl">
              <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)] mb-6">
                The technology lifecycle platform
              </div>
              <h1 className="font-heading text-[48px] md:text-[56px] leading-[1.02] tracking-[-0.02em] font-medium">
                Every<br />
                technology asset.<br />
                <span className="inline-flex items-baseline gap-3 flex-wrap">
                  <span aria-hidden="true" className="st-word-morph" role="presentation">
                    {STAGES.map((w, i) => (
                      <span key={w} style={{ "--i": i }}>{w}</span>
                    ))}
                    {/* Reserve width for the widest word so the layout never shifts */}
                    <span className="st-word-morph-ghost" aria-hidden="true">Recovered</span>
                  </span>
                  <span className="text-[color:var(--st-text-muted)] font-normal">as one.</span>
                </span>
                {/* Accessible label — spoken to SR users; hidden visually */}
                <span className="sr-only">
                  Every technology asset procured, deployed, managed, supported, recovered, reused and recycled as one.
                </span>
              </h1>
              <p className="mt-6 text-[15px] leading-[1.7] text-[color:var(--st-text-muted)] max-w-[52ch]">
                Laptops, desktops, mobiles, tablets, servers, networking, peripherals, accessories, IoT and more.
              </p>
              <p className="mt-4 text-[14px] leading-[1.7] text-[color:var(--st-text-muted)] max-w-[46ch]">
                From technology requirement to responsible end-of-life, ONE keeps every stage connected.
              </p>
            </div>

            {/* Lifecycle rail — hairline chain with a gliding marker */}
            <div className="mt-14">
              <div className="relative">
                <div className="h-px bg-[color:var(--st-border-strong)]" />
                <div className="st-rail-marker" aria-hidden="true" />
              </div>
              <div className="mt-4 grid grid-cols-7 gap-1 text-[10px] uppercase tracking-[0.16em] text-[color:var(--st-text-subtle)]">
                {["Procure", "Deploy", "Manage", "Support", "Recover", "Reuse", "Recycle"].map((s, i) => (
                  <span key={s} className="st-rail-label text-center" style={{ "--i": i }}>{s}</span>
                ))}
              </div>
            </div>
          </div>

          {/* AUTH SIDE — deliberately calm & compact */}
          <div className="lg:col-span-5 lg:pl-8 lg:border-l lg:border-[color:var(--st-border)] flex items-center">
            <div className="w-full max-w-[380px]">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--st-text-muted)]">Welcome</div>
              <h2 className="font-heading text-[22px] font-medium tracking-[-0.01em] mt-1">Sign in to ONE</h2>
              <p className="text-[13px] text-[color:var(--st-text-muted)] mt-1">
                Access your organisation&apos;s technology workspace.
              </p>

              {a.step === "email" ? (
                <form onSubmit={a.continueEmail} className="mt-8 space-y-4">
                  <Field label="Work email" type="email" value={a.email} onChange={a.setEmail}
                    required autoFocus autoComplete="email" placeholder="name@company.com" testId="e-email" />
                  {a.error && <ErrorMsg msg={a.error} />}
                  <button type="submit" disabled={a.loading || !a.email}
                    className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium inline-flex items-center justify-center gap-2 disabled:opacity-50 hover:bg-[#17263C] transition-colors"
                    data-testid="e-continue">
                    {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (<>Continue <ArrowRight className="h-4 w-4" strokeWidth={1.6} /></>)}
                  </button>
                </form>
              ) : (
                <form onSubmit={a.submitPassword} className="mt-8 space-y-4">
                  <button type="button" onClick={a.backToEmail}
                    className="text-[12px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)] inline-flex items-center gap-1">
                    <ChevronLeft className="h-3.5 w-3.5" /> {a.email}
                  </button>
                  <Field label="Password" value={a.password} onChange={a.setPassword} type="password"
                    required autoFocus autoComplete="current-password" testId="e-password"
                    trailing={<Link to="/forgot-password" className="text-[11px] text-[color:var(--st-text-muted)] hover:text-[color:var(--st-text)]">Forgot password?</Link>} />
                  {a.error && <ErrorMsg msg={a.error} />}
                  <button type="submit" disabled={a.loading}
                    className="w-full h-11 rounded-md bg-[color:var(--st-text)] text-white font-medium disabled:opacity-50 inline-flex items-center justify-center gap-2 hover:bg-[#17263C] transition-colors">
                    {a.loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Sign in"}
                  </button>
                </form>
              )}
            </div>
          </div>
        </div>
      </main>

      <footer className="border-t border-[color:var(--st-border)] py-5">
        <div className="max-w-6xl mx-auto px-6 md:px-8 flex justify-between text-[11px] text-[color:var(--st-text-subtle)]">
          <span>© 2026 Suntronix. All rights reserved.</span>
          <div className="flex gap-6">
            <a href="/help/trust" className="hover:text-[color:var(--st-text)]">Security &amp; Privacy</a>
            <a href="/help/accessibility" className="hover:text-[color:var(--st-text)]">Accessibility</a>
            <a href="/help/status" className="hover:text-[color:var(--st-text)]">System status</a>
            <a href="/help" className="hover:text-[color:var(--st-text)]">Help</a>
          </div>
        </div>
      </footer>
    </div>
  );
}

function Field({ label, value, onChange, trailing, testId, ...p }) {
  return (
    <label className="block">
      <div className="flex items-baseline justify-between">
        <span className="text-[11px] uppercase tracking-[0.14em] text-[color:var(--st-text-muted)]">{label}</span>
        {trailing}
      </div>
      <input {...p} value={value} onChange={(e) => onChange(e.target.value)} data-testid={testId}
        className="mt-2 w-full h-11 px-3 rounded-md border border-[color:var(--st-border)] bg-white text-[15px] text-[color:var(--st-text)] placeholder:text-[color:var(--st-text-hint)] focus:outline-none focus:border-[color:var(--st-text)] focus:ring-2 focus:ring-[color:var(--st-text)]/10" />
    </label>
  );
}
function ErrorMsg({ msg }) {
  return (
    <div role="alert" data-testid="e-error"
      className="text-[12px] text-[color:var(--st-critical)] bg-[color:var(--st-critical-soft)] border border-[color:var(--st-critical-soft)] rounded-md px-3 py-2">
      {msg}
    </div>
  );
}
